import { Link } from "react-router-dom";
import { useSeo } from "../lib/seo";
import { IMAGES, PROFILE } from "../data/content";

const List = ({ title, items, render, testid }) => (
  <div data-testid={testid}>
    <h3 className="font-display text-2xl">{title}</h3>
    <ul className="mt-4 border-l-4 border-brass pl-6 space-y-4">{items.map(render)}</ul>
  </div>
);

const Story = () => (
  <section className="bg-cream text-ink" data-testid="about-story">
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-[1fr_1.2fr] gap-12 items-start">
      <figure className="w-full max-w-md">
        <div className="aspect-[4/5] bg-sand rounded-t-[999px] overflow-hidden relative wp">
          <img src={IMAGES.portrait.src} alt={IMAGES.portrait.alt} className="absolute inset-0 w-full h-full object-cover object-top" />
        </div>
        <figcaption className="lbl mt-4 text-slate">Michelle Andros, Owner and Consultant</figcaption>
      </figure>
      <div className="min-w-0">
        <p className="lbl text-teal fd">About</p>
        <h1 className="mt-6"><span className="ln"><span>My story</span></span></h1>
        <div className="mt-8 space-y-5 max-w-prose fd f1">
          <p>I'm Michelle Andros, owner of Deaf Education Consulting LLC. I studied the Education of Persons with Hearing Loss at Indiana University of Pennsylvania, and I've spent my career working with Deaf and Hard of Hearing students and the people who support them.</p>
          <p>I taught D/HH students in Virginia and Pennsylvania, served as an itinerant teacher of the Deaf with Allegheny Intermediate Unit #3, and worked as an educational consultant with PaTTAN on D/HH services, secondary transition, inclusive practices, procedural safeguards and assistive technology. I then led external education at DePaul School for Hearing and Speech, and today I coordinate itinerant services at the Western Pennsylvania School for the Deaf.</p>
          <p>Because I've worked inside schools and alongside families, I understand both sides of the IEP process. I'm a certified teacher of the Deaf, sign language proficient, a See the Sound Visual Phonics Trainer, and an Ambassador to Eduplex Institute in South Africa.</p>
        </div>
        <Link to="/contact#consultation" className="mt-8 inline-flex font-sans bg-teal text-paper rounded-full px-7 py-3.5 hover:bg-teal-deep transition-colors" data-testid="about-consultation-cta">Request a consultation</Link>
      </div>
    </div>
  </section>
);

const Credentials = () => (
  <section className="bg-sky text-ink py-24 md:py-32" aria-labelledby="cred-title" data-testid="about-credentials">
    <div className="max-w-6xl mx-auto px-6">
      <h2 id="cred-title" className="font-display">Credentials</h2>
      <div className="mt-12 grid md:grid-cols-2 gap-14">
        <List title="Licensure & certification" testid="about-licensure" items={PROFILE.licensure}
          render={(c) => <li key={c.title}><p>{c.title}</p><p className="font-sans text-sm text-slate">{c.year}</p></li>} />
        <List title="Education" testid="about-education" items={PROFILE.education}
          render={(c) => <li key={c.title}><p>{c.title}</p><p className="font-sans text-sm text-slate">{c.where} · {c.year}</p></li>} />
        <List title="Professional memberships" testid="about-memberships" items={PROFILE.memberships}
          render={(m) => <li key={m}>{m}</li>} />
        <List title="Honors" testid="about-honors" items={["Dean's List, Indiana University of Pennsylvania (1992, 1994, 1995, 1996)", "Graduated with honors, Shenandoah University (2001)"]}
          render={(m) => <li key={m}>{m}</li>} />
      </div>
    </div>
  </section>
);

const Experience = () => (
  <section className="bg-cream text-ink py-24 md:py-32" aria-labelledby="exp-title" data-testid="about-experience">
    <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1fr_2fr] gap-12">
      <h2 id="exp-title" className="font-display">Experience</h2>
      <ol className="border-b border-ink/25">
        {PROFILE.experience.map((e) => (
          <li key={e.years + e.where} className="border-t border-ink/25 py-5 grid sm:grid-cols-[9rem_1fr] gap-2">
            <span className="font-sans text-sm text-teal font-semibold pt-1" style={{ fontVariantNumeric: "tabular-nums" }}>{e.years}</span>
            <div><p className="font-display text-xl">{e.role}</p><p className="text-slate">{e.where}</p></div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Talks = () => (
  <section className="bg-paper text-ink py-24 md:py-32" aria-labelledby="talks-title" data-testid="about-talks">
    <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1fr_2fr] gap-12">
      <div>
        <h2 id="talks-title" className="font-display">Selected talks & courses</h2>
        <Link to="/speaking" className="mt-6 inline-flex font-sans link-u text-teal" data-testid="about-speaking-cta">Invite Michelle to speak</Link>
      </div>
      <ul className="space-y-5">
        {PROFILE.talks.map((t) => (
          <li key={t.text} className="flex gap-5"><span className="font-sans text-sm text-brass font-semibold w-20 shrink-0 pt-1">{t.year}</span><span>{t.text}</span></li>
        ))}
      </ul>
    </div>
  </section>
);

export default function About() {
  useSeo("About Michelle Andros", "Michelle Andros is a certified teacher of the Deaf, sign language proficient, a See the Sound Visual Phonics Trainer and owner of Deaf Education Consulting LLC, licensed in Pennsylvania.");
  return (<><Story /><Credentials /><Experience /><Talks /></>);
}
